import { useState, useEffect, useCallback } from 'react';
import { dashboardService, type Territory, type TerritoryPlace } from '../../../services/dashboard.service';

export interface UseTerritoriesApi {
  territories: Territory[];
  loading: boolean;
  isAdmin: boolean;
  create: (label: string, owner: string) => Promise<Territory>;
  update: (id: string, patch: { label?: string; owner?: string }) => Promise<void>;
  remove: (id: string) => Promise<void>;
  addPlace: (territoryId: string, place: TerritoryPlace) => Promise<void>;
  removePlace: (territoryId: string, placeId: string) => Promise<void>;
}

export const useTerritoriesApi = (): UseTerritoriesApi => {
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Checking if user has admin role to manage territories
  const isAdmin = localStorage.getItem('role') === 'admin';

  const fetchTerritories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await dashboardService.getTerritories();
      setTerritories(res.data || []);
    } catch (err) {
      console.error('Failed to fetch territories', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTerritories();
  }, [fetchTerritories]);

  const create = async (label: string, owner: string) => {
    const res = await dashboardService.createTerritory({ label, owner });
    const newTerritory = res.data;
    setTerritories(prev => [...prev, newTerritory].sort((a, b) => a.label.localeCompare(b.label)));
    return newTerritory;
  };

  const update = async (id: string, patch: { label?: string; owner?: string }) => {
    await dashboardService.updateTerritory(id, patch);
    setTerritories(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
  };

  const remove = async (id: string) => {
    await dashboardService.deleteTerritory(id);
    setTerritories(prev => prev.filter(t => t.id !== id));
  };

  const addPlace = async (territoryId: string, place: TerritoryPlace) => {
    await dashboardService.addTerritoryPlace(territoryId, place);
    setTerritories(prev => prev.map(t => {
      if (t.id === territoryId) {
        // Prevent duplicates
        if (t.places.some(p => p.id === place.id)) return t;
        return { ...t, places: [...t.places, place] };
      }
      return t;
    }));
  };

  const removePlace = async (territoryId: string, placeId: string) => {
    await dashboardService.removeTerritoryPlace(territoryId, placeId);
    setTerritories(prev => prev.map(t => {
      if (t.id === territoryId) {
        return { ...t, places: t.places.filter(p => p.id !== placeId) };
      }
      return t;
    }));
  };

  return {
    territories,
    loading,
    isAdmin,
    create,
    update,
    remove,
    addPlace,
    removePlace,
  };
};
